import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useTranslation } from '@/i18n';
import { useUI } from '@/hooks/ui/useUI';
import { useCatalogLoader } from '@/hooks/ui/useCatalogLoader';
import { useSidePanelLoading } from '@/hooks/ui/useSidePanelLoading';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { attendanceConceptService } from '@/api/domains/attendance';
import { attendanceConceptCatalogService } from '@/api/domains/catalog/attendanceConceptCatalog';
import type {
  AttendanceConceptHandling,
  AttendanceConceptFormula,
} from '@/api/domains/catalog/attendanceConceptCatalog';
import type { AttendanceConceptDetail } from '@/api/domains/attendance';
import { injectValidationErrors } from '@/utils/formValidationErrors';
import {
  updateAttendanceConceptSchema,
  type UpdateAttendanceConceptFormData,
} from '@/schemas/attendance/updateAttendanceConceptForm';


interface UseEditAttendanceConceptPanelParams {
  concept: AttendanceConceptDetail | null;
  onSaved?: () => void;
  readOnly?: boolean;
}

export interface UseEditAttendanceConceptPanelResult {
  handlingValue: string;
  calculationFormulaValue: string;
  obtentionFormulaValue: string;
  handlingOptions: { value: string; label: string }[];
  formulaOptions: { value: string; label: string }[];
  isSubmitting: boolean;
  isReady: boolean;
  isReadOnly: boolean;
  errors: ReturnType<typeof useForm<UpdateAttendanceConceptFormData>>['formState']['errors'];
  isSubmitted: boolean;
  handleHandlingChange: (value: string | string[]) => void;
  handleCalculationFormulaChange: (value: string | string[]) => void;
  handleObtentionFormulaChange: (value: string | string[]) => void;
  handleSubmit: (event?: FormEvent<HTMLFormElement>) => void;
}

export function useEditAttendanceConceptPanel({
  concept,
  onSaved,
  readOnly,
}: UseEditAttendanceConceptPanelParams): UseEditAttendanceConceptPanelResult {
  const { t: translate } = useTranslation();
  const { showSuccess, showApiError, showError, closeSidePanel } = useUI();
  const { setLoading } = useSidePanelLoading();
  const { canEdit } = usePagePermissions();

  const isReadOnly = readOnly === true || !canEdit;
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // ── Local field state ──────────────────────────────────────────────────
  const [handlingValue, setHandlingValue] = useState<string>('');
  const [calculationFormulaValue, setCalculationFormulaValue] = useState<string>('');
  const [obtentionFormulaValue, setObtentionFormulaValue] = useState<string>('');

  // Sync concept values when concept loads (concept arrives async after mount)
  useEffect(() => {
    if (!concept) return;
    setHandlingValue(concept.handlingId != null ? String(concept.handlingId) : '');
    setCalculationFormulaValue(concept.calculationFormulaId != null ? String(concept.calculationFormulaId) : '');
    setObtentionFormulaValue(concept.obtentionFormulaId != null ? String(concept.obtentionFormulaId) : '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [concept?.id]);

  // ── Catalog loading ────────────────────────────────────────────────────
  const handlingCatalog = useCatalogLoader<AttendanceConceptHandling>(
    (signal) => attendanceConceptCatalogService.getHandlings(signal),
    (item) => ({ value: String(item.id), label: item.name }),
    [],
    false,
    () => showError(translate('attendance:attendanceConcept.messages.catalogLoadError')),
  );

  const formulaCatalog = useCatalogLoader<AttendanceConceptFormula>(
    (signal) => attendanceConceptCatalogService.getFormulas(signal),
    (item) => ({ value: String(item.id), label: item.name }),
    [],
    false,
    () => showError(translate('attendance:attendanceConcept.messages.catalogLoadError')),
  );

  const catalogsLoaded = handlingCatalog.isLoaded && formulaCatalog.isLoaded;

  useEffect(() => {
    setLoading(!catalogsLoaded);
  }, [catalogsLoaded, setLoading]);

  // ── React Hook Form ────────────────────────────────────────────────────
  const {
    handleSubmit: rhfHandleSubmit,
    setValue,
    setError,
    register,
    unregister,
    formState: { errors, isSubmitted },
  } = useForm<UpdateAttendanceConceptFormData>({
    resolver: zodResolver(updateAttendanceConceptSchema),
    mode: 'onSubmit',
    defaultValues: {
      handlingId: concept?.handlingId ?? null,
      calculationFormulaId: concept?.calculationFormulaId ?? null,
      obtentionFormulaId: concept?.obtentionFormulaId ?? null,
    },
  });

  // Register fields manually (local-state-driven pattern)
  useEffect(() => {
    register('handlingId');
    register('calculationFormulaId');
    register('obtentionFormulaId');
    return () => {
      unregister('handlingId');
      unregister('calculationFormulaId');
      unregister('obtentionFormulaId');
    };
  }, [register, unregister]);

  // Sync local state → form values
  useEffect(() => {
    setValue('handlingId', handlingValue ? Number(handlingValue) : null);
  }, [handlingValue, setValue]);

  useEffect(() => {
    setValue('calculationFormulaId', calculationFormulaValue ? Number(calculationFormulaValue) : null);
  }, [calculationFormulaValue, setValue]);

  useEffect(() => {
    setValue('obtentionFormulaId', obtentionFormulaValue ? Number(obtentionFormulaValue) : null);
  }, [obtentionFormulaValue, setValue]);

  // ── isReady ────────────────────────────────────────────────────────────
  const isReady = useMemo(
    () =>
      catalogsLoaded &&
      handlingValue.length > 0 &&
      calculationFormulaValue.length > 0 &&
      obtentionFormulaValue.length > 0,
    [catalogsLoaded, handlingValue, calculationFormulaValue, obtentionFormulaValue],
  );

  // ── Handlers ───────────────────────────────────────────────────────────
  const handleHandlingChange = useCallback((value: string | string[]) => {
    setHandlingValue(Array.isArray(value) ? (value[0] ?? '') : value);
  }, []);

  const handleCalculationFormulaChange = useCallback((value: string | string[]) => {
    setCalculationFormulaValue(Array.isArray(value) ? (value[0] ?? '') : value);
  }, []);

  const handleObtentionFormulaChange = useCallback((value: string | string[]) => {
    setObtentionFormulaValue(Array.isArray(value) ? (value[0] ?? '') : value);
  }, []);

  // ── Submit ─────────────────────────────────────────────────────────────
  const onValidSubmit = useCallback(
    async (values: UpdateAttendanceConceptFormData) => {
      if (!concept?.id) return;
      setIsSubmitting(true);

      try {
        const response = await attendanceConceptService.update(concept.id, {
          handlingId: values.handlingId as number,
          calculationFormulaId: values.calculationFormulaId as number,
          obtentionFormulaId: values.obtentionFormulaId as number,
        });

        if (!response.success) {
          if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
            injectValidationErrors(response.error.validationErrors, { setError });
            return;
          }
          showApiError(response, translate('attendance:attendanceConcept.messages.updateError'));
          return;
        }

        showSuccess(translate('attendance:attendanceConcept.messages.updateSuccess'));
        closeSidePanel();
        onSaved?.();
      } finally {
        setIsSubmitting(false);
      }
    },
    [concept?.id, setError, showApiError, showSuccess, closeSidePanel, onSaved, translate],
  );

  const handleSubmit = useCallback(
    (event?: FormEvent<HTMLFormElement>) => {
      event?.preventDefault();
      if (isReadOnly) return;
      void rhfHandleSubmit(onValidSubmit)(event as unknown as React.BaseSyntheticEvent);
    },
    [rhfHandleSubmit, onValidSubmit, isReadOnly],
  );

  return {
    handlingValue,
    calculationFormulaValue,
    obtentionFormulaValue,
    handlingOptions: handlingCatalog.options,
    formulaOptions: formulaCatalog.options,
    isSubmitting,
    isReady,
    isReadOnly,
    errors,
    isSubmitted,
    handleHandlingChange,
    handleCalculationFormulaChange,
    handleObtentionFormulaChange,
    handleSubmit,
  };
}

export default useEditAttendanceConceptPanel;
